import { type InputHTMLAttributes, useEffect, useState } from "react";
import { cx } from "../cx";
import styles from "./TimeInput.module.css";
import { formatTime, parseTime, type TimeFormat } from "./time";

export interface TimeInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "defaultValue" | "type"> {
  /** Minutes after midnight. */
  value: number | null;
  onChange: (minutes: number) => void;
  format?: TimeFormat;
  /** Borderless until hover/focus, like the tracker row inputs. */
  inline?: boolean;
}

/** Free-text time box: parses on blur/Enter, reverts on invalid input or Escape. */
export function TimeInput({
  value,
  onChange,
  format = "12h",
  inline,
  className,
  onBlur,
  onKeyDown,
  ...rest
}: TimeInputProps) {
  const formatted = value === null ? "" : formatTime(value, format);
  const [text, setText] = useState(formatted);
  useEffect(() => setText(formatted), [formatted]);

  const commit = () => {
    const parsed = parseTime(text);
    if (parsed === null) {
      setText(formatted);
      return;
    }
    setText(formatTime(parsed, format));
    if (parsed !== value) onChange(parsed);
  };

  return (
    <input
      type="text"
      inputMode="text"
      autoComplete="off"
      className={cx(styles.input, inline && styles.inline, className)}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={(e) => {
        commit();
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        else if (e.key === "Escape") setText(formatted);
        onKeyDown?.(e);
      }}
      {...rest}
    />
  );
}
